import { useEffect, useRef, useState } from "react";
import { vpAxios } from "../api";
import { toast } from "sonner";
import { ExternalLink, Copy, Upload, Image as ImageIcon, CreditCard, MessageCircle, Sparkles, Calendar, CheckCircle2, XCircle, ArrowUpRight } from "lucide-react";

export default function ConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [me, setMe] = useState(null);
  const [suporteWhats, setSuporteWhats] = useState("");
  const [planos, setPlanos] = useState([]);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = () => vpAxios.get("/vaipede/loja/config").then(r => setCfg(r.data));
  useEffect(() => {
    load();
    vpAxios.get("/vaipede/auth/me").then(r => setMe(r.data)).catch(() => {});
    vpAxios.get("/vaipede/planos-publico").then(r => {
      setSuporteWhats(r.data.suporte_whatsapp || "");
      setPlanos(r.data.planos || []);
    }).catch(() => {});
  }, []);

  const assinar = async (planoId) => {
    try {
      const { data } = await vpAxios.post("/vaipede/checkout", { plano_id: planoId });
      if (data.init_point) window.location.href = data.init_point;
      else toast.info(data.message || "Checkout indisponível");
    } catch (e) { toast.error("Falha ao criar checkout"); }
  };

  const salvar = async () => {
    try {
      const payload = {
        ...cfg,
        taxa_servico_valor: parseFloat(cfg.taxa_servico_valor || 0) || 0,
        taxa_couvert_valor: parseFloat(cfg.taxa_couvert_valor || 0) || 0,
      };
      await vpAxios.put("/vaipede/loja/config-full-v2", payload);
      toast.success("Salvo!");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Logo muito grande (máx. 2.5MB)"); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const { data } = await vpAxios.post("/vaipede/uploads/imagem", { data_url: dataUrl });
      setCfg(c => ({...c, logo_url: data.url}));
      toast.success("Logo enviado. Clique em Salvar.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha no upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const cardapioUrl = cfg?.slug ? `${window.location.origin}/vaipede/cardapio/${cfg.slug}` : "";

  if (!cfg) return <div>Carregando...</div>;

  return (
    <div data-testid="vaipede-config" className="max-w-2xl">
      <h1 className="text-2xl font-black mb-6">Configurações</h1>

      {/* Dados da loja */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-4 space-y-3">
        <h2 className="font-bold">Dados da loja</h2>
        <label className="block">
          <span className="text-xs text-neutral-500">Nome da loja</span>
          <input value={cfg.loja_nome || ""} onChange={e => setCfg({...cfg, loja_nome: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm"/>
        </label>
        <label className="block">
          <span className="text-xs text-neutral-500">WhatsApp (com DDD)</span>
          <input value={cfg.whatsapp || ""} onChange={e => setCfg({...cfg, whatsapp: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm"/>
        </label>
        <label className="block">
          <span className="text-xs text-neutral-500">Endereço</span>
          <input value={cfg.endereco || ""} onChange={e => setCfg({...cfg, endereco: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm"/>
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-neutral-500">CNPJ (aparece no recibo)</span>
            <input value={cfg.cnpj || ""} onChange={e => setCfg({...cfg, cnpj: e.target.value})} placeholder="00.000.000/0000-00" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm font-mono" data-testid="vaipede-cfg-cnpj"/>
          </label>
          <label className="block">
            <span className="text-xs text-neutral-500">Razão social (opcional)</span>
            <input value={cfg.razao_social || ""} onChange={e => setCfg({...cfg, razao_social: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm"/>
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-neutral-500">Descrição / horário</span>
          <textarea value={cfg.descricao || ""} onChange={e => setCfg({...cfg, descricao: e.target.value})} rows={2} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm resize-none"/>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={cfg.aberto !== false} onChange={e => setCfg({...cfg, aberto: e.target.checked})}/>
          Loja aberta (visível no cardápio digital)
        </label>
      </div>

      {/* Logo */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-4">
        <h2 className="font-bold mb-2">Logo da loja</h2>
        <p className="text-xs text-neutral-500 mb-3">Usada no cardápio digital e como ícone do app quando o cliente instala.</p>
        <div className="flex items-center gap-4">
          {cfg.logo_url ? (
            <img src={cfg.logo_url} alt="Logo" className="w-24 h-24 rounded-2xl object-cover border border-neutral-200"/>
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-neutral-100 grid place-items-center text-neutral-400"><ImageIcon className="w-8 h-8"/></div>
          )}
          <div className="flex-1">
            <input type="file" accept="image/*" ref={fileRef} onChange={uploadLogo} className="hidden" data-testid="vaipede-cfg-logo-input"/>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="px-4 py-2.5 rounded-xl bg-neutral-100 font-bold text-sm flex items-center gap-2" data-testid="vaipede-cfg-logo-btn">
              <Upload className="w-4 h-4"/> {uploading ? "Enviando..." : (cfg.logo_url ? "Trocar logo" : "Adicionar logo")}
            </button>
            {cfg.logo_url && <button onClick={() => setCfg({...cfg, logo_url: ""})} className="ml-2 text-xs text-red-500">Remover</button>}
          </div>
        </div>
      </div>

      {/* Integração InfinitePay */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-4">
        <h2 className="font-bold mb-2 flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-600"/> Integração InfinitePay</h2>
        <p className="text-xs text-neutral-500 mb-3">Sua InfiniteTag para receber pagamentos online (Pix/Crédito/Débito) direto do cardápio digital e do PDV.</p>
        <label className="block">
          <span className="text-xs text-neutral-500">InfiniteTag (handle)</span>
          <input value={cfg.infinitepay_handle || ""} onChange={e => setCfg({...cfg, infinitepay_handle: e.target.value})} placeholder="ex: minha-loja-123" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm font-mono" data-testid="vaipede-cfg-infinitepay"/>
        </label>
        <p className="text-xs text-neutral-500 mt-2">
          Não tem uma? <a href="https://infinitepay.io" target="_blank" rel="noreferrer" className="text-[#EA1D2C] underline">Crie sua conta aqui</a>. Se ficar em branco, o pagamento cai na conta central do VAI PEDE.
        </p>
        <label className="block mt-3">
          <span className="text-xs text-neutral-500">Serial da maquininha (opcional)</span>
          <input value={cfg.impressora_maquininha_serial || ""} onChange={e => setCfg({...cfg, impressora_maquininha_serial: e.target.value})} placeholder="Deixe em branco se ainda não tem" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm font-mono"/>
        </label>
      </div>

      {/* Taxas globais (Serviço + Couvert) */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-4">
        <h2 className="font-bold mb-1">Taxas nas mesas</h2>
        <p className="text-xs text-neutral-500 mb-4">Quando ativas, valem para TODAS as mesas. No fechamento você pode remover pontualmente se quiser.</p>

        <div className="border border-neutral-200 rounded-xl p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <b className="text-sm">Taxa de serviço (garçom)</b>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!cfg.taxa_servico_ativa} onChange={e => setCfg({...cfg, taxa_servico_ativa: e.target.checked})} data-testid="vaipede-cfg-taxa-servico-toggle"/>
              <span className="text-xs">{cfg.taxa_servico_ativa ? "Ativa" : "Desativada"}</span>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={cfg.taxa_servico_tipo || "percentual"} onChange={e => setCfg({...cfg, taxa_servico_tipo: e.target.value})} disabled={!cfg.taxa_servico_ativa} className="px-3 py-2 rounded-xl border border-neutral-200 text-sm disabled:opacity-50">
              <option value="percentual">% Percentual</option>
              <option value="fixo">R$ Fixo</option>
            </select>
            <input type="text" inputMode="decimal" value={cfg.taxa_servico_valor ?? ""} onChange={e => setCfg({...cfg, taxa_servico_valor: e.target.value.replace(",", ".")})} disabled={!cfg.taxa_servico_ativa} placeholder="Valor" className="px-3 py-2 rounded-xl border border-neutral-200 text-sm disabled:opacity-50" data-testid="vaipede-cfg-taxa-servico-valor"/>
          </div>
        </div>

        <div className="border border-neutral-200 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <b className="text-sm">Couvert artístico</b>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!cfg.taxa_couvert_ativa} onChange={e => setCfg({...cfg, taxa_couvert_ativa: e.target.checked})} data-testid="vaipede-cfg-taxa-couvert-toggle"/>
              <span className="text-xs">{cfg.taxa_couvert_ativa ? "Ativo" : "Desativado"}</span>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={cfg.taxa_couvert_tipo || "fixo"} onChange={e => setCfg({...cfg, taxa_couvert_tipo: e.target.value})} disabled={!cfg.taxa_couvert_ativa} className="px-3 py-2 rounded-xl border border-neutral-200 text-sm disabled:opacity-50">
              <option value="fixo">R$ Fixo</option>
              <option value="percentual">% Percentual</option>
            </select>
            <input type="text" inputMode="decimal" value={cfg.taxa_couvert_valor ?? ""} onChange={e => setCfg({...cfg, taxa_couvert_valor: e.target.value.replace(",", ".")})} disabled={!cfg.taxa_couvert_ativa} placeholder="Valor" className="px-3 py-2 rounded-xl border border-neutral-200 text-sm disabled:opacity-50" data-testid="vaipede-cfg-taxa-couvert-valor"/>
          </div>
        </div>
      </div>

      {/* Link do cardápio */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-4">
        <h2 className="font-bold mb-2">Link público do cardápio</h2>
        {cardapioUrl && (
          <div className="flex items-center gap-2">
            <a href={cardapioUrl} target="_blank" rel="noreferrer" className="flex-1 text-sm text-[#EA1D2C] hover:underline truncate">{cardapioUrl}</a>
            <button onClick={() => { navigator.clipboard.writeText(cardapioUrl); toast.success("Link copiado"); }} className="p-2 bg-neutral-100 rounded-lg"><Copy className="w-4 h-4"/></button>
            <a href={cardapioUrl} target="_blank" rel="noreferrer" className="p-2 bg-neutral-100 rounded-lg"><ExternalLink className="w-4 h-4"/></a>
          </div>
        )}
        <p className="text-xs text-neutral-500 mt-2">O cliente pode instalar seu cardápio como app no celular direto pelo navegador.</p>
      </div>

      {/* Minha Assinatura */}
      {me && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-4" data-testid="vaipede-cfg-assinatura">
          <h2 className="font-bold mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#EA1D2C]"/> Minha assinatura
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-neutral-200 p-3">
              <div className="text-xs text-neutral-500 mb-1">Plano</div>
              <div className="font-black text-lg">
                {me.super_admin ? "Premium (Super Admin)" : (me.plano_nome || me.plano || "Trial")}
              </div>
              {me.plano_valor && (
                <div className="text-xs text-neutral-500 mt-1">
                  R$ {Number(me.plano_valor).toFixed(2).replace(".", ",")}/mês
                </div>
              )}
            </div>
            <div className="rounded-xl border border-neutral-200 p-3">
              <div className="text-xs text-neutral-500 mb-1">Status</div>
              <div className="flex items-center gap-2">
                {me.assinatura_ativa ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-emerald-600"/>
                    <span className="font-black text-emerald-700">Ativa</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-red-500"/>
                    <span className="font-black text-red-600">Inativa</span>
                  </>
                )}
              </div>
              {me.bloqueado && <div className="text-xs text-red-500 mt-1">Acesso bloqueado</div>}
            </div>
            <div className="rounded-xl border border-neutral-200 p-3 sm:col-span-2">
              <div className="text-xs text-neutral-500 mb-1 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5"/> {me.plano === "trial" ? "Fim do trial gratuito" : "Próxima renovação automática"}
              </div>
              <div className="font-bold text-sm">
                {me.plano === "trial" && me.trial_ate ? (
                  <span className={me.trial_dias_restantes <= 2 ? "text-red-600" : "text-amber-600"}>
                    {me.trial_expirado
                      ? "Expirado"
                      : `${me.trial_dias_restantes === 1 ? "1 dia restante" : `${me.trial_dias_restantes} dias restantes`} · até ${new Date(me.trial_ate).toLocaleDateString("pt-BR")}`}
                  </span>
                ) : me.proxima_renovacao
                  ? new Date(me.proxima_renovacao).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
                  : (me.super_admin ? "Vitalícia (Super Admin)" : "—")}
              </div>
              {me.assinatura_iniciada_em && (
                <div className="text-xs text-neutral-500 mt-1">
                  Assinatura iniciada em {new Date(me.assinatura_iniciada_em).toLocaleDateString("pt-BR")}
                </div>
              )}
            </div>
          </div>
          {!me.super_admin && (
            <div className="mt-4">
              <div className="text-xs font-bold text-neutral-500 mb-2 uppercase tracking-wide">
                {me.assinatura_ativa ? "Fazer upgrade / trocar plano" : "Escolha seu plano"}
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {planos.map(p => {
                  const isAtual = me.plano === p.id;
                  const destaque = p.destaque;
                  return (
                    <div
                      key={p.id}
                      className={`relative rounded-2xl p-4 border-2 flex flex-col ${
                        isAtual ? "border-emerald-500 bg-emerald-50" :
                        destaque ? "border-[#EA1D2C] bg-white shadow-sm" :
                        "border-neutral-200 bg-white"
                      }`}
                      data-testid={`vaipede-cfg-plano-${p.id}`}
                    >
                      {isAtual && (
                        <span className="absolute -top-2 left-3 bg-emerald-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                          SEU PLANO
                        </span>
                      )}
                      {destaque && !isAtual && (
                        <span className="absolute -top-2 left-3 bg-[#EA1D2C] text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                          MAIS POPULAR
                        </span>
                      )}
                      <div className="font-black text-base">{p.nome}</div>
                      <div className="text-2xl font-black mt-1">R$ {Number(p.preco).toFixed(2).replace(".", ",")}<span className="text-xs font-normal text-neutral-500">/mês</span></div>
                      <div className="text-[11px] text-neutral-500 mt-1 mb-3">{p.teto}</div>
                      <ul className="text-xs space-y-1 mb-3 flex-1">
                        {(p.recursos || []).slice(0, 5).map((r, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5"/>
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                      {!isAtual && (
                        <button
                          onClick={() => assinar(p.id)}
                          className={`w-full py-2 rounded-lg text-xs font-black flex items-center justify-center gap-1 ${
                            destaque ? "bg-[#EA1D2C] hover:bg-[#c8121f] text-white" :
                            "bg-neutral-900 hover:bg-black text-white"
                          }`}
                          data-testid={`vaipede-cfg-assinar-${p.id}`}
                        >
                          <ArrowUpRight className="w-3.5 h-3.5"/>
                          {me.assinatura_ativa ? `Trocar para ${p.nome}` : `Assinar ${p.nome}`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Suporte Técnico */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-4" data-testid="vaipede-cfg-suporte">
        <h2 className="font-bold mb-2 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-600"/> Suporte técnico
        </h2>
        <p className="text-xs text-neutral-500 mb-3">
          Precisa de ajuda? Fale direto com a equipe do VAI PEDE pelo WhatsApp — resposta rápida em horário comercial.
        </p>
        <a
          href={suporteWhats ? `https://wa.me/${suporteWhats.replace(/\D/g, "")}?text=${encodeURIComponent(`Olá, sou ${me?.nome || "assinante VAI PEDE"} da loja ${me?.loja_nome || ""}. Preciso de suporte técnico.`)}` : "#"}
          target="_blank"
          rel="noreferrer"
          onClick={e => { if (!suporteWhats) { e.preventDefault(); toast.info("Suporte indisponível no momento"); }}}
          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm"
          data-testid="vaipede-cfg-suporte-btn"
        >
          <MessageCircle className="w-4 h-4"/> Falar com o suporte no WhatsApp
        </a>
      </div>

      <button onClick={salvar} className="w-full py-3 rounded-xl bg-[#EA1D2C] text-white font-bold" data-testid="vaipede-cfg-salvar">Salvar configurações</button>
    </div>
  );
}
